import { parseFragment, Tokenizer, TokenizerMode, type Token } from 'parse5';

export type PreparedGeneratedHtml = {
  html: string;
  isPlainText: boolean;
  repaired: boolean;
  errorCodes: string[];
};

const FORMATTING_TAGS = new Set([
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'del',
  'mark',
  'small',
  'sub',
  'sup',
]);
const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);
const RAW_TEXT_TAGS = new Set([
  'style',
  'xmp',
  'iframe',
  'noembed',
  'noframes',
]);
const LITERAL_TAGS = new Set([
  'code',
  'pre',
  'script',
  'style',
  'textarea',
  'title',
  'xmp',
  'iframe',
  'noembed',
  'noframes',
  'plaintext',
  'svg',
  'math',
]);

// KaTeX expressions are parsed separately by the renderer. Keep offsets intact.
const maskMath = (html: string): string =>
  html.replace(/\$\$[\s\S]*?\$\$/g, (expression: string): string =>
    expression.replace(/[^\r\n]/g, ' '),
  );

const findClosingTagRepair = (html: string): number | undefined => {
  const openTags: string[] = [];
  let repairOffset: number | undefined;
  const ignore = (): void => undefined;
  const tokenizer = new Tokenizer(
    { sourceCodeLocationInfo: true },
    {
      onComment: ignore,
      onDoctype: ignore,
      onEof: ignore,
      onCharacter: ignore,
      onNullCharacter: ignore,
      onWhitespaceCharacter: ignore,
      onStartTag: (token: Token.TagToken): void => {
        if (!VOID_TAGS.has(token.tagName)) openTags.push(token.tagName);
        if (token.tagName === 'script')
          tokenizer.state = TokenizerMode.SCRIPT_DATA;
        else if (RAW_TEXT_TAGS.has(token.tagName))
          tokenizer.state = TokenizerMode.RAWTEXT;
        else if (token.tagName === 'textarea' || token.tagName === 'title')
          tokenizer.state = TokenizerMode.RCDATA;
        else if (token.tagName === 'plaintext')
          tokenizer.state = TokenizerMode.PLAINTEXT;
      },
      onEndTag: (token: Token.TagToken): void => {
        const location = token.location;
        if (
          location &&
          !openTags.some((tag: string): boolean => LITERAL_TAGS.has(tag))
        ) {
          const source = html.slice(location.startOffset, location.endOffset);
          const name = /^<\/([a-z][a-z0-9]*)/i.exec(source)?.[1];
          if (
            name &&
            FORMATTING_TAGS.has(name.toLowerCase()) &&
            openTags.at(-1) === name.toLowerCase()
          ) {
            const delimiterOffset =
              location.startOffset + '</'.length + name.length;
            const tail = html.slice(delimiterOffset, location.endOffset);
            const followingText = tail.split('<', 1)[0];
            if (
              /^[\s.,;:!?<]/.test(tail) &&
              !/^\s*>$/.test(tail) &&
              !/[="']/.test(followingText)
            ) {
              repairOffset = delimiterOffset;
              tokenizer.pause();
              return;
            }
          }
        }
        const matchingIndex = openTags.lastIndexOf(token.tagName);
        if (matchingIndex >= 0) openTags.length = matchingIndex;
      },
    },
  );
  tokenizer.write(maskMath(html), true);
  return repairOffset;
};

/** Repair only matching formatting end tags; preserve the original on ambiguity. */
export const prepareGeneratedHtml = (
  original: string,
): PreparedGeneratedHtml => {
  let html = original;
  let repairOffset = findClosingTagRepair(html);
  while (repairOffset !== undefined) {
    html = `${html.slice(0, repairOffset)}>${html.slice(repairOffset)}`;
    repairOffset = findClosingTagRepair(html);
  }
  const errorCodes = new Set<string>();
  parseFragment(maskMath(html), {
    onParseError: (error): void => {
      errorCodes.add(error.code);
    },
  });
  const isPlainText = errorCodes.size > 0;
  return {
    html: isPlainText ? original : html,
    isPlainText,
    repaired: !isPlainText && html !== original,
    errorCodes: [...errorCodes],
  };
};
